<template>
  <div
    class="date-input"
    :style="{
      '--q-primary': `rgb(var(--tw-color-${props.primaryColor}))`,
    }"
  >
    <div
      v-if="props.showTrigger"
      :class="{
        'date-input__trigger--dense': props.dense,
        'date-input--error': props.hasError,
        'date-input__trigger--readonly': props.readonly,
      }"
      class="date-input__trigger"
      :tabindex="props.readonly ? -1 : 0"
      @mouseenter="hoveringOverInput = true"
      @mouseleave="hoveringOverInput = false"
      @click="(e) => showPopup(e.currentTarget as HTMLElement)"
      @keydown.enter="(e) => showPopup(e.currentTarget as HTMLElement)"
      @keydown.space="(e) => showPopup(e.currentTarget as HTMLElement)"
    >
      <span
        v-if="props.prependLabel"
        class="input-prepend-label tw--ml-3.5 tw-mr-3"
      >
        {{ props.prependLabel }}
      </span>

      <span
        :class="{
          'date-input__date-placeholder': !localDate,
        }"
        class="date-input__date"
      >
        {{ dateLabel }}
      </span>

      <div class="date-input__right-action">
        <octicon
          :class="{ 'date-input__close-icon--visible': showCloseIcon }"
          class="date-input__close-icon"
          icon="x-circle"
          @click.stop="clearInput"
        />
        <octicon class="date-input__icon" icon="calendar" />
      </div>
    </div>

    <span
      v-if="props.hasError && props.errorMessage && props.showTrigger"
      class="date-input__error-message"
    >
      {{ props.errorMessage }}
    </span>

    <q-dialog
      v-model="isPopupOpen"
      position="standard"
      @show="onShow"
      @hide="onHide"
      @before-hide="onBeforeHide"
    >
      <div
        v-drag-dialog
        class="dialog__wrapper single-column__wrapper tw-flex tw-min-h-0 tw-flex-col tw-bg-white"
        :style="{
          '--q-primary': `rgb(var(--tw-color-${props.primaryColor}))`,
          ...cssVars,
          width: '380px',
          maxWidth: '95vw',
        }"
        @keydown.esc.stop="handleEscapeKey"
        @keydown="handleDialogKeydown"
        @click="handleModalClick"
      >
        <div
          class="dialog__header-wrapper"
          :style="{
            '--header-color': `rgb(var(--tw-color-${props.primaryColor}))`,
            borderTopColor: `rgb(var(--tw-color-${props.primaryColor}))`,
          }"
        >
          <div class="dialog__header tw-flex tw-w-full tw-flex-col">
            <div class="tw-flex tw-w-full tw-items-center tw-justify-between">
              <div class="tw-flex tw-min-w-0 tw-flex-1 tw-items-center">
                <span class="dialog__title">{{ props.title }}</span>
                <span
                  v-if="
                    props.clearable &&
                    (props.modelValue || props.forceShowClearButton)
                  "
                  v-close-popup
                  class="actions-link tw-ml-3"
                  @click="onClearClick"
                >
                  Clear
                </span>
              </div>
              <p-dialog-close-icon @click="closePopup" />
            </div>
            <div class="tw-flex tw-w-full tw-items-center tw-py-3">
              <q-input
                id="dateInput"
                ref="dateInputRef"
                v-model="inputDate"
                class="input-xs tw-flex-1 tw-bg-white"
                dense
                outlined
                :placeholder="`Enter date (${dateTimeFormat.date})`"
                autofocus
                @focus="selectAll"
                @keydown.enter="handleEnterKey"
                @keydown.escape.stop="handleEscapeKey"
                @keydown.tab="handleDateInputTab"
                @keydown.up.prevent="handleDateInputArrow('up')"
                @keydown.down.prevent="handleDateInputArrow('down')"
              >
              </q-input>
              <p-btn
                class="tw-ml-2 tw-shrink-0 tw-rounded tw-py-1.5 tw-text-xs"
                :color="props.primaryColor"
                tabindex="2"
                @click="setDateToToday"
              >
                <span class="text-weight-regular">Today</span>
              </p-btn>
            </div>
          </div>
        </div>

        <div class="dialog__body tw-flex-1 tw-overflow-visible">
          <q-date
            ref="qDateRef"
            v-model="localDate"
            minimal
            flat
            no-unset
            :style="{
              '--q-primary': `rgb(var(--tw-color-${props.primaryColor}))`,
            }"
            class="date-input__calendar tw-w-full tw-bg-white"
            :tabindex="isCalendarFocused ? 0 : -1"
            v-bind="props.qDateProps"
            @update:model-value="handleDatePickerChange"
            @navigation="handleNavigation"
            @focus="onCalendarFocus"
            @blur="onCalendarBlur"
            @keydown="handleCalendarKeydown"
          >
            <slot name="presets">
              <!-- TODO: in the future, provide default presets like "today", "last week", etc.  -->
              <!-- <div class="tw-mb-2">
              <q-btn
                :style="{ backgroundColor: appColors['gray-light'] }"
                unelevated
                size="sm"
              >
                Today
              </q-btn>
            </div> -->
            </slot>
          </q-date>

          <!-- time input start -->
          <div
            v-if="allowTime"
            class="tw-border-t tw-border-gray-medium-light tw-px-4 tw-pt-3"
          >
            <q-toggle
              v-if="allowTime === 'optional'"
              v-model="showTimeInputWhenOptional"
              left-label
              label="Add time"
              class="tw-mb-3"
            />

            <div
              v-if="allowTime !== 'optional' || showTimeInputWhenOptional"
              class="tw-w-full"
            >
              <label class="input-label tw-mb-2 tw-block">Time</label>
              <div class="date-input__time-input-container tw-w-full">
                <octicon
                  icon="clock"
                  :height="16"
                  class="tw-ml-3 tw-text-gray-medium-dark"
                />
                <div class="tw-flex tw-flex-1 tw-items-center tw-px-3">
                  <input
                    ref="hourInputRef"
                    :value="localTime.hour"
                    type="number"
                    min="1"
                    max="12"
                    step="1"
                    class="date-input__time-field tw-bg-white"
                    tabindex="3"
                    @input="padZero"
                    @focus="selectAll"
                    @blur="handleHourChange"
                    @keydown="handleTimeKeydown($event, 'hour')"
                    @keydown.escape.stop="handleEscapeKey"
                  />
                  <span class="tw-text-gray-dark">:</span>
                  <input
                    ref="minuteInputRef"
                    :value="localTime.minute"
                    type="number"
                    min="0"
                    max="59"
                    step="1"
                    class="date-input__time-field tw-bg-white"
                    tabindex="4"
                    @input="padZero"
                    @focus="selectAll"
                    @blur="handleMinuteChange"
                    @keydown="handleTimeKeydown($event, 'minute')"
                    @keydown.escape.stop="handleEscapeKey"
                  />
                  <button
                    class="date-input__meridian-button"
                    tabindex="5"
                    @click="handleMeridianChange"
                    @keydown="handleMeridianKeydown"
                    @keydown.escape.stop="handleEscapeKey"
                  >
                    {{ localTime.meridian }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <!-- time input end -->
        </div>

        <!-- Footer with Done button -->
        <div
          v-if="allowTime"
          class="dialog__footer tw-border-t tw-border-gray-light tw-p-3"
        >
          <p-btn
            color="primary"
            class="tw-w-full"
            tabindex="6"
            @click="onDoneClick"
          >
            Done
          </p-btn>
        </div>
      </div>
    </q-dialog>
  </div>
</template>

<script setup lang="ts">
defineOptions({ inheritAttrs: false });
import {
  computed,
  HTMLAttributes,
  nextTick,
  reactive,
  ref,
  watch,
  defineExpose,
} from 'vue';
import Octicon from '@/components/core/icons/Octicon.vue';
import dayjs from '@/utils/dayjs';
import { useIntl } from '@/utils/internationalization';
import { QDate, QDateProps, QInput } from 'quasar';
import {
  convertTo24Hour,
  formatLabel,
  toServerDate,
  toServerDateTime,
} from '@/utils/date';
import { timeout } from '@/utils/time';
import isEqual from 'lodash-es/isEqual';
import * as chrono from 'chrono-node';
import { organizationData } from '@/store/helpers';
import { Colors } from '@/api';
import { useRoot } from '@/utils/component';

interface Props {
  modelValue: string | null | undefined;
  title?: string;
  placeholder?: string;
  noPlaceholder?: boolean;
  clearable?: boolean;
  dense?: boolean;
  errorMessage?: string;
  hasError?: boolean;
  allowTime?: boolean | 'optional';
  inputClass?: HTMLAttributes['class'];
  triggerContainerClass?: HTMLAttributes['class'];
  prependLabel?: string;
  primaryColor?: Colors;
  defaultTimeOfDay?: 'start' | 'end';
  forceShowClearButton?: boolean;
  qDateProps?: Partial<QDateProps>;
  isDialog?: boolean;
  dontUseWordForSelectedDate?: boolean;
  readonly?: boolean;
  showTrigger?: boolean;
  closeOnSelect?: boolean;
}
interface Emits {
  (e: 'update:modelValue', value: string | null | undefined): void;
  (e: 'change', value: string): void;
  (e: 'clear'): void;
  (e: 'hide'): void;
}

const props = withDefaults(defineProps<Props>(), {
  noPlaceholder: false,
  clearable: false,
  dense: false,
  hasError: false,
  triggerContainerClass: '',
  primaryColor: Colors.BLUE,
  defaultTimeOfDay: 'end',
  title: 'Select a date',
  qDateProps: () => ({}),
  showTrigger: true,
  closeOnSelect: false,
});
const emit = defineEmits<Emits>();
const hourInputRef = ref<HTMLInputElement | null>(null);
const minuteInputRef = ref<HTMLInputElement | null>(null);
const dateInputRef = ref<InstanceType<typeof QInput>>();
const qDateRef = ref<InstanceType<typeof QDate>>();

const { dateTimeFormat } = useIntl();
const hoveringOverInput = ref(false);
const localDate = ref('');
const isPopupOpen = ref(false);
const isPopupClosing = ref(false);
const isClosingFromKeyboard = ref(false);
const { user } = organizationData();
const root = useRoot();

const inputDate = ref('');
const cursorDate = ref<string | null>(null);
const triggerElement = ref<HTMLElement | null>(null);
const isCalendarFocused = ref(false);

function parseDate(dateStr: string) {
  if (user.value && user.value.dateFormat === 'en-GB') {
    return chrono.en.GB.parseDate(dateStr);
  }
  return chrono.parseDate(dateStr);
}

function showPopup(trigger?: HTMLElement) {
  if (props.readonly) return;
  if (trigger) {
    triggerElement.value = trigger;
  }
  isPopupOpen.value = true;
}

function closePopup() {
  isPopupOpen.value = false;
}

async function onDoneClick() {
  if (preparedModelValue.value) {
    emit('update:modelValue', preparedModelValue.value);
    emit('change', preparedModelValue.value as string);
  }
  closePopup();
}

async function setDateToToday() {
  const today = dayjs();
  inputDate.value = today.format(dateTimeFormat.value.date);
  setLocalDateFromInput();

  // Wait for Vue to update computed properties
  await nextTick();

  // If time input is not visible, save and close immediately
  const isTimeInputVisible =
    props.allowTime &&
    (props.allowTime !== 'optional' || showTimeInputWhenOptional.value);

  if (!isTimeInputVisible) {
    if (preparedModelValue.value) {
      emit('update:modelValue', preparedModelValue.value);
      emit('change', preparedModelValue.value as string);
    }
    closePopup();
  } else {
    // DateTime mode: move focus to time input so user can adjust time if needed
    hourInputRef.value?.focus();
    hourInputRef.value?.select();
  }
}

watch(
  isPopupOpen,
  async (open, wasOpen) => {
    if (open) {
      isPopupClosing.value = false;
      const val = root.$store.item.alphaNumericInput
        ? root.$store.item.alphaNumericInput
        : props.modelValue;
      if (val) {
        const dt = dayjs(val as string);
        if (dt.isValid()) {
          inputDate.value = dt.format(
            root.$store.item.alphaNumericInput
              ? root.$store.item.alphaNumericInput
              : dateTimeFormat.value.date
          );
        } else {
          inputDate.value = '';
        }
      }
    } else {
      // Manually trigger onBeforeHide since QDialog @before-hide doesn't fire reliably
      if (wasOpen) {
        onBeforeHide();
      }

      root.$store.item.clearAlphaNumericInput();
      isPopupClosing.value = true;
      await timeout(500);
      isPopupClosing.value = false;
    }
  },
  { flush: 'sync' }
);

// time handling start

interface ILocalTime {
  hour: string;
  minute: string;
  second: string;
  millisecond: string;
  meridian: 'AM' | 'PM';
}
const localTimeDefaults = (): ILocalTime => {
  if (props.defaultTimeOfDay === 'start') {
    return {
      hour: '12',
      minute: '00',
      second: '00',
      millisecond: '000',
      meridian: 'AM',
    };
  } else
    return {
      hour: '11',
      minute: '59',
      second: '59',
      millisecond: '999',
      meridian: 'PM',
    };
};
const localTime = reactive<ILocalTime>(localTimeDefaults());
const showTimeInputWhenOptional = ref(false);

watch(showTimeInputWhenOptional, () => {
  if (!showTimeInputWhenOptional.value) {
    Object.assign(localTime, localTimeDefaults());
  }
});

const hasDefaultTime = computed(() => isEqual(localTime, localTimeDefaults()));
watch(
  () => hasDefaultTime.value,
  (val) => {
    if (!val) showTimeInputWhenOptional.value = true;
    else if (!isPopupOpen.value) showTimeInputWhenOptional.value = false;
  },
  { immediate: true }
);

function isInputValueWithinConstraints(
  value: string | number,
  constraints: { min?: number; max?: number; step?: number }
): boolean {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return false;
  if (constraints.min && num < constraints.min) return false;
  if (constraints.max && num > constraints.max) return false;
  if (constraints.step && num % constraints.step !== 0) return false;
  return true;
}

async function handleHourChange(evt: Event) {
  const value = (evt.target as HTMLInputElement).value;
  const isValid = isInputValueWithinConstraints(
    (evt.target as HTMLInputElement).value,
    { min: 1, max: 12, step: 1 }
  );
  if (!isValid) {
    (evt.target as HTMLInputElement).value = localTime.hour;
    return;
  }
  localTime.hour = parseInt(value, 10).toString().padStart(2, '0');
}
async function handleMinuteChange(evt: Event) {
  const value = (evt.target as HTMLInputElement).value;
  const isValid = isInputValueWithinConstraints(
    (evt.target as HTMLInputElement).value,
    { min: 0, max: 59, step: 1 }
  );
  if (!isValid) {
    (evt.target as HTMLInputElement).value = localTime.minute;
    return;
  }
  localTime.minute = parseInt(value, 10).toString().padStart(2, '0');
}
async function handleMeridianChange() {
  localTime.meridian = localTime.meridian === 'AM' ? 'PM' : 'AM';
}
async function padZero(evt: Event) {
  const target = evt.target as HTMLInputElement;
  const value = parseInt(target.value, 10);
  if (value === 0 || isNaN(value)) return;
  target.value = value.toString().padStart(2, '0');
}
async function selectAll(evt: Event) {
  await timeout(10);
  const input = evt.target as HTMLInputElement;
  if (input.value !== root.$store.item.alphaNumericInput) {
    // Select all text in the input field
    input.select();
  } else {
    // Keep cursor at the end
    const length = input.value.length;
    input.setSelectionRange(length, length);
  }
}

// time handling end
function setLocalValueFromModel() {
  const val = props.modelValue;
  if (!val) {
    localDate.value = '';
    inputDate.value = '';
    Object.assign(localTime, localTimeDefaults());
  } else if (props.allowTime) {
    const dt = dayjs(val as string);
    localDate.value = dt.format('YYYY/MM/DD');
    Object.assign(localTime, {
      hour: dt.format('hh'),
      minute: dt.format('mm'),
      second: dt.format('ss'),
      meridian: dt.format('A'),
    });
  } else {
    localDate.value = dayjs(val as string).format('YYYY/MM/DD');
  }
}
watch(() => props.modelValue, setLocalValueFromModel, {
  immediate: true,
  deep: true,
});

const dateLabel = computed(() => {
  const defaultPlaceholder = (() => {
    if (typeof props.placeholder === 'string') return props.placeholder;
    if (props.readonly) return '';
    return 'Select a date';
  })();
  if (!preparedModelValue.value) return props.placeholder || defaultPlaceholder;

  const dt = dayjs(preparedModelValue.value as string);
  if (!dt.isValid()) return defaultPlaceholder;
  if (props.allowTime || !hasDefaultTime.value) {
    return dt.format(dateTimeFormat.value.fullDateTime);
  }
  return formatLabel(
    preparedModelValue.value as string,
    props.dontUseWordForSelectedDate || false
  );
});

const showCloseIcon = computed(() => {
  return (
    hoveringOverInput.value &&
    props.clearable &&
    !!localDate.value &&
    !props.readonly
  );
});

const preparedModelValue = computed(() => {
  if (!localDate.value) return '';
  if (props.allowTime) {
    const hourIn24Format = convertTo24Hour(localTime.hour, localTime.meridian);
    const minute = +localTime.minute;
    const second = +localTime.second;
    const millisecond = +localTime.millisecond;

    const date = dayjs(`${localDate.value}`)
      .hour(hourIn24Format)
      .minute(minute)
      .second(second)
      .millisecond(millisecond)
      .toDate();

    return toServerDateTime(date);
  } else {
    return toServerDate(dayjs(localDate.value).toDate());
  }
});

function setLocalDateFromInput() {
  const parsedDate = parseDate(inputDate.value);
  if (parsedDate) {
    localDate.value = dayjs(parsedDate.toISOString() as string).format(
      'YYYY/MM/DD'
    );
  } else {
    localDate.value = '';
  }
}

function onBeforeHide() {
  // Return focus to trigger element
  // Use double requestAnimationFrame to ensure focus happens AFTER all updates
  if (
    triggerElement.value &&
    typeof triggerElement.value.focus === 'function' &&
    document.contains(triggerElement.value)
  ) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (triggerElement.value && document.contains(triggerElement.value)) {
          triggerElement.value.focus();
        }
      });
    });
  }
}

async function onHide() {
  // Clear cursor date
  cursorDate.value = null;

  // Always reset to original values on hide
  // The Done button will handle saving changes
  setLocalValueFromModel();

  // Emit hide event so parent components can respond to dialog close
  emit('hide');
}

async function onShow() {
  // Initialize cursor date to current value or today
  cursorDate.value = localDate.value || dayjs().format('YYYY/MM/DD');

  // Focus on the date input field
  await nextTick();
  const input = dateInputRef.value?.$el.querySelector('input');
  if (input) {
    input.focus();
    input.select();
  }

  // Apply initial cursor styling
  await nextTick();
  updateCursorStyling();
}

async function handleEnterKey(evt: KeyboardEvent) {
  // Prevent event propagation
  evt.stopPropagation();
  evt.preventDefault();

  await nextTick();

  // If the input date is valid, update the local date
  const parsedDate = parseDate(inputDate.value);
  if (parsedDate) {
    localDate.value = dayjs(parsedDate).format('YYYY/MM/DD');
  }

  // Always save and close when pressing Enter
  if (preparedModelValue.value) {
    emit('update:modelValue', preparedModelValue.value);
    emit('change', preparedModelValue.value as string);
  }
  closePopup();
}

function handleEscapeKey() {
  // Reset to original values and close
  setLocalValueFromModel();
  closePopup();
}

function handleDateInputTab(evt: KeyboardEvent) {
  if (!evt.shiftKey) {
    // Prevent default and focus the calendar
    evt.preventDefault();
    focusCalendar();
  }
}

async function handleDateInputArrow(direction: 'up' | 'down') {
  // Initialize cursor date if not set
  if (!cursorDate.value) {
    cursorDate.value = localDate.value || dayjs().format('YYYY/MM/DD');
  }

  // Calculate new cursor date (up = -7 days, down = +7 days)
  const cursor = dayjs(cursorDate.value);
  const newCursor =
    direction === 'up' ? cursor.subtract(7, 'day') : cursor.add(7, 'day');

  const newCursorDate = newCursor.format('YYYY/MM/DD');

  // Check if we need to change months
  const oldMonth = cursor.month();
  const oldYear = cursor.year();
  const newMonth = newCursor.month() + 1;
  const newYear = newCursor.year();
  const isChangingMonth =
    oldMonth !== newCursor.month() || oldYear !== newCursor.year();

  // Update the cursor date
  cursorDate.value = newCursorDate;

  // Focus the calendar
  await focusCalendar();

  // Navigate to new month if needed
  if (isChangingMonth) {
    qDateRef.value?.setCalendarTo(newYear, newMonth);
    // Wait for month transition animation
    setTimeout(() => {
      updateCursorStyling();
    }, 350);
  } else {
    updateCursorStyling();
  }
}

async function focusCalendar() {
  // Initialize cursor date if not set
  if (!cursorDate.value) {
    cursorDate.value = localDate.value || dayjs().format('YYYY/MM/DD');
  }

  isCalendarFocused.value = true;

  await nextTick();

  // Focus the calendar element
  const calendarEl = qDateRef.value?.$el;
  if (calendarEl) {
    calendarEl.focus();
  }

  updateCursorStyling();
}

function onCalendarFocus() {
  isCalendarFocused.value = true;
  if (!cursorDate.value) {
    cursorDate.value = localDate.value || dayjs().format('YYYY/MM/DD');
  }
  updateCursorStyling();
}

function onCalendarBlur() {
  isCalendarFocused.value = false;
}

function handleModalClick(evt: MouseEvent) {
  // Initialize cursor date when clicking anywhere in the modal (except inputs)
  const target = evt.target as HTMLElement;
  const isInput =
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'BUTTON';

  if (!isInput && !cursorDate.value) {
    cursorDate.value = localDate.value || dayjs().format('YYYY/MM/DD');
    updateCursorStyling();
  }
}

async function handleDatePickerChange(val: string) {
  const date = dayjs(val);
  inputDate.value = date.format(dateTimeFormat.value.date);

  // If closeOnSelect is true, save and close immediately
  if (props.closeOnSelect) {
    // Wait for Vue to update computed properties
    await nextTick();
    if (preparedModelValue.value) {
      emit('update:modelValue', preparedModelValue.value);
      emit('change', preparedModelValue.value as string);
    }
    closePopup();
    return;
  }

  if (props.allowTime) {
    // With time, wait for Done button
    return;
  }
  // Without time, save immediately and close
  if (preparedModelValue.value) {
    emit('update:modelValue', preparedModelValue.value);
    emit('change', preparedModelValue.value as string);
  }
  closePopup();
}

function clearInput() {
  emit('update:modelValue', '');
  emit('clear');
}

function onClearClick() {
  emit('clear');
}

const cssVars = computed(() => {
  const primaryColor = `rgb(var(--tw-color-${props.primaryColor}))`;
  return {
    '--primary-color': primaryColor,
    '--scrollbar-color': `color-mix(in srgb, ${primaryColor} 80%, white)`,
    '--q-primary-rgb': `var(--tw-color-${props.primaryColor})`,
  } as Record<string, string>;
});

function handleTimeKeydown(evt: KeyboardEvent, field: 'hour' | 'minute') {
  const input = evt.target as HTMLInputElement;
  const currentValue = parseInt(input.value, 10) || 0;

  if (evt.key === 'ArrowUp') {
    evt.preventDefault();
    if (field === 'hour') {
      const newHour = currentValue === 12 ? 1 : currentValue + 1;
      localTime.hour = newHour.toString().padStart(2, '0');
      input.value = localTime.hour;
    } else {
      const newMinute = currentValue === 59 ? 0 : currentValue + 1;
      localTime.minute = newMinute.toString().padStart(2, '0');
      input.value = localTime.minute;
    }
  } else if (evt.key === 'ArrowDown') {
    evt.preventDefault();
    if (field === 'hour') {
      const newHour = currentValue === 1 ? 12 : currentValue - 1;
      localTime.hour = newHour.toString().padStart(2, '0');
      input.value = localTime.hour;
    } else {
      const newMinute = currentValue === 0 ? 59 : currentValue - 1;
      localTime.minute = newMinute.toString().padStart(2, '0');
      input.value = localTime.minute;
    }
  } else if (evt.key === 'Enter') {
    evt.preventDefault();
    evt.stopPropagation();
    // Save and close from any time input field
    onDoneClick();
  } else if (evt.key === 'Tab') {
    if (!evt.shiftKey && field === 'minute') {
      // Move focus to meridian button
      evt.preventDefault();
      const meridianButton = input.parentElement?.querySelector(
        '.date-input__meridian-button'
      ) as HTMLElement;
      meridianButton?.focus();
    } else if (evt.shiftKey && field === 'hour') {
      // Shift+Tab from hour field goes to calendar
      evt.preventDefault();
      focusCalendar();
    }
  }
}

function handleMeridianKeydown(evt: KeyboardEvent) {
  if (evt.key === 'ArrowUp' || evt.key === 'ArrowDown' || evt.key === ' ') {
    evt.preventDefault();
    handleMeridianChange();
  } else if (evt.key === 'Enter') {
    evt.preventDefault();
    evt.stopPropagation();
    onDoneClick();
  } else if (evt.key === 'Tab' && evt.shiftKey) {
    // Move focus back to minute input
    evt.preventDefault();
    minuteInputRef.value?.focus();
    minuteInputRef.value?.select();
  }
}

async function handleDialogKeydown(evt: KeyboardEvent) {
  const target = evt.target as HTMLElement;
  const isInInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
  const isDateInput = target.id === 'dateInput';
  const isTimeInput = target.classList.contains('date-input__time-field');
  const isMeridianButton = target.classList.contains(
    'date-input__meridian-button'
  );

  // For time inputs and meridian button, don't handle arrow keys here
  if (isTimeInput || isMeridianButton) {
    return;
  }

  // For date input, only allow arrow keys for calendar navigation
  if (
    isDateInput &&
    !['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(evt.key)
  ) {
    return;
  }

  // For any other input that's not date input, skip
  if (isInInput && !isDateInput) {
    return;
  }

  // Handle calendar navigation with arrow keys and Enter
  if (
    ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(
      evt.key
    )
  ) {
    // Initialize cursor date if not set
    if (!cursorDate.value) {
      cursorDate.value = localDate.value || dayjs().format('YYYY/MM/DD');
    }

    // Process the keydown for calendar navigation
    evt.preventDefault();
    evt.stopPropagation();

    const cursor = dayjs(cursorDate.value);
    let newCursor = cursor;

    switch (evt.key) {
      case 'ArrowUp':
        newCursor = cursor.subtract(7, 'day');
        break;
      case 'ArrowDown':
        newCursor = cursor.add(7, 'day');
        break;
      case 'ArrowLeft':
        newCursor = cursor.subtract(1, 'day');
        break;
      case 'ArrowRight':
        newCursor = cursor.add(1, 'day');
        break;
      case 'Enter':
        // Only process Enter if not in an input field
        if (!isInInput) {
          localDate.value = cursorDate.value;
          inputDate.value = dayjs(cursorDate.value).format(
            dateTimeFormat.value.date
          );

          // If closeOnSelect is true, save and close immediately
          if (props.closeOnSelect) {
            // Wait for Vue to update computed properties
            await nextTick();
            if (preparedModelValue.value) {
              emit('update:modelValue', preparedModelValue.value);
              emit('change', preparedModelValue.value as string);
            }
            isClosingFromKeyboard.value = true;
            closePopup();
            return;
          }

          // Check if time input is visible
          const isTimeInputVisible =
            props.allowTime &&
            (props.allowTime !== 'optional' || showTimeInputWhenOptional.value);

          if (!isTimeInputVisible) {
            // Date-only mode: save and close immediately
            if (preparedModelValue.value) {
              emit('update:modelValue', preparedModelValue.value);
              emit('change', preparedModelValue.value as string);
            }
            closePopup();
          } else {
            // DateTime mode: move focus to time input
            hourInputRef.value?.focus();
            hourInputRef.value?.select();
          }
        }
        return;
    }

    // Update cursor date and navigate if needed
    const newCursorDate = newCursor.format('YYYY/MM/DD');
    const oldMonth = cursor.month();
    const oldYear = cursor.year();
    const newMonth = newCursor.month() + 1;
    const newYear = newCursor.year();

    const isChangingMonth =
      oldMonth !== newCursor.month() || oldYear !== newCursor.year();

    if (isChangingMonth) {
      qDateRef.value?.setCalendarTo(newYear, newMonth);
    }

    cursorDate.value = newCursorDate;

    // Update cursor styling
    if (isChangingMonth) {
      setTimeout(() => {
        updateCursorStyling();
      }, 350);
    } else {
      updateCursorStyling();
    }
  }
}

async function handleCalendarKeydown(evt: KeyboardEvent) {
  // Only handle specific keys when calendar is focused
  if (!isCalendarFocused.value) return;

  // Handle Tab navigation
  if (evt.key === 'Tab') {
    if (evt.shiftKey) {
      // Shift+Tab - go back to date input
      evt.preventDefault();
      dateInputRef.value?.$el.querySelector('input')?.focus();
    } else {
      // Tab - go to time input if visible, otherwise let default behavior
      const isTimeInputVisible =
        props.allowTime &&
        (props.allowTime !== 'optional' || showTimeInputWhenOptional.value);
      if (isTimeInputVisible) {
        evt.preventDefault();
        hourInputRef.value?.focus();
        hourInputRef.value?.select();
      }
    }
    return;
  }

  // Handle Escape
  if (evt.key === 'Escape') {
    handleEscapeKey();
    return;
  }

  // Initialize cursor date if not set
  if (!cursorDate.value) {
    cursorDate.value = localDate.value || dayjs().format('YYYY/MM/DD');
  }

  // Only handle arrow keys and Enter
  if (
    !['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(
      evt.key
    )
  ) {
    return;
  }

  evt.preventDefault();
  evt.stopPropagation();

  const cursor = dayjs(cursorDate.value);
  let newCursor = cursor;

  switch (evt.key) {
    case 'ArrowUp':
      newCursor = cursor.subtract(7, 'day');
      break;
    case 'ArrowDown':
      newCursor = cursor.add(7, 'day');
      break;
    case 'ArrowLeft':
      newCursor = cursor.subtract(1, 'day');
      break;
    case 'ArrowRight':
      newCursor = cursor.add(1, 'day');
      break;
    case 'Enter': {
      // Select the cursor date
      localDate.value = cursorDate.value;
      inputDate.value = dayjs(cursorDate.value).format(
        dateTimeFormat.value.date
      );

      // If closeOnSelect is true, save and close immediately
      if (props.closeOnSelect) {
        // Wait for Vue to update computed properties
        await nextTick();
        if (preparedModelValue.value) {
          emit('update:modelValue', preparedModelValue.value);
          emit('change', preparedModelValue.value as string);
        }
        isClosingFromKeyboard.value = true;
        closePopup();
        return;
      }

      // Check if time input is visible
      const isTimeInputVisible =
        props.allowTime &&
        (props.allowTime !== 'optional' || showTimeInputWhenOptional.value);

      if (!isTimeInputVisible) {
        // Date-only mode: save and close immediately
        if (preparedModelValue.value) {
          emit('update:modelValue', preparedModelValue.value);
          emit('change', preparedModelValue.value as string);
        }
        closePopup();
      } else {
        // DateTime mode: move focus to time input
        hourInputRef.value?.focus();
        hourInputRef.value?.select();
      }
      return;
    }
  }

  // Update cursor date and navigate if needed
  const newCursorDate = newCursor.format('YYYY/MM/DD');
  const oldMonth = cursor.month();
  const oldYear = cursor.year();
  const newMonth = newCursor.month() + 1;
  const newYear = newCursor.year();

  // Check if we're changing months
  const isChangingMonth =
    oldMonth !== newCursor.month() || oldYear !== newCursor.year();

  if (isChangingMonth) {
    qDateRef.value?.setCalendarTo(newYear, newMonth);
  }

  cursorDate.value = newCursorDate;

  // Update cursor styling
  if (isChangingMonth) {
    // Wait for month transition animation
    setTimeout(() => {
      updateCursorStyling();
    }, 350);
  } else {
    updateCursorStyling();
  }
}

function handleNavigation() {
  // When user navigates months, update cursor styling
  nextTick(() => {
    updateCursorStyling();
  });
}

function updateCursorStyling() {
  removeCursorStyling();

  if (!cursorDate.value || !qDateRef.value) {
    return;
  }

  // Find the calendar day button for cursor date
  const cursorDay = dayjs(cursorDate.value).date();

  // Try multiple selectors to find calendar days
  let calendarDays = qDateRef.value.$el.querySelectorAll(
    '.q-date__calendar-item--in'
  );

  if (calendarDays.length === 0) {
    // Try alternative selectors
    calendarDays = qDateRef.value.$el.querySelectorAll(
      '.q-date__calendar-item'
    );
  }

  if (calendarDays.length === 0) {
    // Try button selector
    calendarDays = qDateRef.value.$el.querySelectorAll(
      '.q-date__calendar-days button'
    );
  }

  if (calendarDays.length === 0) {
    // Try a more specific selector
    calendarDays = qDateRef.value.$el.querySelectorAll(
      '.q-date__calendar-days-container button'
    );
  }

  let foundMatch = false;
  calendarDays.forEach((dayEl: HTMLElement) => {
    const dayNum = parseInt(dayEl.textContent || '0', 10);
    if (dayNum === cursorDay && !foundMatch) {
      dayEl.classList.add('date-input__cursor');
      foundMatch = true;
    }
  });
}

function removeCursorStyling() {
  if (!qDateRef.value) return;

  const cursorElements = qDateRef.value.$el.querySelectorAll(
    '.date-input__cursor'
  );
  cursorElements.forEach((el: HTMLElement) => {
    el.classList.remove('date-input__cursor');
  });
}

defineExpose({ showPopup, closePopup });
</script>

<style lang="scss">
@import '../../../styles/mixins';

.date-input {
  @apply tw-relative;

  &__trigger {
    @apply tw-border tw-border-gray-medium tw-rounded-sm tw-flex tw-items-center tw-px-3 tw-w-full tw-cursor-pointer tw-bg-white;

    border-radius: 4px; // match Quasar inputs
    height: 40px;

    &--dense {
      height: 32px;
    }

    &--readonly {
      @apply tw-border-dashed tw-cursor-auto;
    }

    &:hover:not(&--readonly) {
      @apply tw-border-gray-dark;
    }

    // Remove focus ring - form elements only use border color, not outline
    &:focus,
    &:focus-visible {
      outline: none !important;
      border-color: var(--q-primary) !important;
    }
  }

  &--error {
    @apply tw-border-red;
    .date-input__date {
      @apply tw-text-red;
    }
    .date-input__icon {
      @apply tw-text-red;
    }
  }

  &__icon {
    @apply tw-w-4 tw-text-gray-medium-dark tw-mb-0.5;
  }

  &__date {
    @apply tw-leading-none tw-text-xs tw-text-gray-dark tw-mt-0.5;
  }

  &__date-placeholder {
    @apply tw-text-gray-medium;
  }

  &__right-action {
    @apply tw-ml-auto tw-flex tw-content-end tw-items-center;
  }

  &__close-icon {
    @apply tw-w-4 tw-text-gray-medium tw-opacity-0 tw-pointer-events-none;

    &--visible {
      @apply tw-opacity-100 tw-pointer-events-auto tw-cursor-pointer tw-ml-2 tw-mr-1;

      &:hover {
        @apply tw-text-primary;
      }
    }
  }

  &__popup {
    @apply tw-relative tw-text-xs;
    .q-date__calendar {
      min-height: 270px;
      padding-bottom: 0;
    }
    .q-date__calendar-days-container {
      height: unset;
      min-height: unset;
    }
  }

  &__time-input-container {
    @apply tw-flex tw-items-center tw-border tw-border-gray-medium tw-rounded tw-overflow-hidden;
    height: 40px; // Match standard input height
    transition: border-color 0.2s, box-shadow 0.2s;

    // Focus-within styling to show focus ring when any child input is focused
    &:focus-within {
      @apply tw-border-primary;
      box-shadow: 0 0 0 3px rgba(var(--q-primary-rgb), 0.2);
    }

    // hide spin box for number inputs (https://stackoverflow.com/a/4298216/6590678)
    input[type='number']::-webkit-outer-spin-button,
    input[type='number']::-webkit-inner-spin-button,
    input[type='number'] {
      -webkit-appearance: none;
      margin: 0;
      -moz-appearance: textfield !important;
    }
  }

  &__time-field {
    width: 2rem;
    @apply tw-ring-0 focus:tw-ring-0 focus:tw-outline-none tw-px-0 tw-py-2 tw-text-center tw-text-sm focus:tw-bg-gray-light;
  }

  &__meridian-button {
    @apply tw-px-2 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-dark hover:tw-bg-gray-light focus:tw-ring-0 focus:tw-outline-none focus:tw-bg-gray-light tw-rounded;
  }

  &__suggestion-wrapper {
    @apply tw-border-r;
  }

  &__suggestion-section {
    @apply tw-flex tw-flex-col tw-py-2 tw-px-4 tw-overflow-y-scroll;

    max-height: 290px;

    @include scrollbars();
  }

  &__error-message {
    @apply tw-text-red tw-block;
    font-size: 10px;
  }

  &__cursor {
    @apply tw-bg-gray-medium-light tw-rounded-full;
  }

  &__calendar {
    @apply tw-p-2;

    &:focus {
      outline: 3px solid var(--q-primary);
      outline-offset: -3px;
      @apply tw-rounded;
    }
  }
}

.actions-link {
  @apply tw-text-sm tw-cursor-pointer tw-text-primary;

  &:hover {
    @apply tw-opacity-80;
  }
}
</style>
